import { ISyncChangesResponse } from '..';

interface ISubject {
  id: number;
  label: string;
  shortCode: string;
}
export interface IQuestionStatus {
  chapter: number;
}

interface IGrade {
  id: number;
  uuid: string;
  gradeOrder: number;
  label: string;
  type: string;
  shortCode: string;
}
interface IChapter {
  id: string;
  title: string;
  chapterNumber: number;
  book: number;
  gradeSubject: number;
  schoolClass: number;
  content: string;
  value: string;
}

interface IGradeSubject {
  id: number;
  uuid: string;
  subject: ISubject;
  grade: IGrade;
}

interface IQuestionMedia {
  url: string;
  type: string;
}

interface IGroupQuestionMedia extends IQuestionMedia {
  imageDescription: string;
}

interface IAnswerOption {
  media: IQuestionMedia[];
  statement: string;
  isCorrect: boolean;
  answerFormat: string;
}
interface ISelectedOptions {
  class?: IGradeSubject;
  subject?: IGradeSubject;
  selectedChapters?: IChapter[];
  examTime: string;
  examType?: string;
}

interface INcpSlo {
  id: number;
  ncpSloId: string;
  sloStatement: string;
  domain: string;
  languageSkills: string | null;
  subStrand: string | null;
  languageCompetency: string | null;
  bloomLevel: string;
  difficultyLevel: string;
  orderLevel: string;
}
interface IBookChapterSlo {
  sloIndex: number;
  ncpSloId: number;
  sloStatement: string;
  ncpSloStatement: string;
}
interface IQuestionGroup {
  id: number;
  comprehensionPassage: string;
  groupType: string;
  created: string;
  modified: string;
  media: IGroupQuestionMedia[];
  titleText: string;
}

export enum GeneratedExamStatus {
  ReadyForUpload = 'READY_FOR_UPLOAD',
  Uploading = 'UPLOADING',
  ReadyForChecking = 'READY_FOR_CHECKING',
  CheckingInProgress = 'CHECKING_IN_PROGRESS',
  Checked = 'CHECKED',
}

export interface IExam {
  id: string;
  date?: string | null;
  time: string | null;
  type: string | null;
  gradeSubject: number;
  schoolClass: number;
  checkpoint: number | null;
  chapters: number[];
  questions: number[];
  totalMarks?: number;
  totalStudents?: number;
  totalPictures?: number;
  status: GeneratedExamStatus;
  created?: string;
  questionsData?: IQuestion[];
  isExamShare?: boolean;
}

interface IQuestion {
  id: number;
  group: IQuestionGroup | null;
  gradeSubject?: IGradeSubject;
  ncpSlo: INcpSlo | null;
  created: string;
  modified: string;
  uuid: string;
  questionStatement: string;
  questionMedia: IQuestionMedia[];
  questionFormat: string;
  score: number;
  markingScheme: string | null;
  type: string;
  subType: string;
  category: string;
  assessmentType: string[];
  supportedLanguages: string[];
  questionStatus: string;
  chapterNumber: number;
  slo: string | null;
  bookChapterSlo: IBookChapterSlo | null;
  questionTags: string[];
  author: string | null;
  isActive: boolean;
  answerOptions: IAnswerOption[];
  bookChapter: number;
  lessonPlans: string[];
  answerLines: number;
}

export enum QuestionTypes {
  Mcq = 'multiple-choice',
  Msq = 'multiple-selection',
  Ftb = 'fill-the-blanks',
  Saq = 'short-answer',
  Laq = 'long-answer',
  Cis = 'correct-the-incorrect-statement',
  Crq = 'constructed-response',
  Rrq = 'restricted-response',
  Reading = 'reading-writing',
  Creative = 'creative-writing',
  Speaking = 'speaking-listening',
  TrueFalse = 'true-false',
  WordsMeanings = 'words-meanings',
  SynonymsAntonyms = 'synonyms-antonyms',
  WordsSentences = 'words-sentences',
  MatchTheColumns = 'match-the-columns',
  Investigate = 'investigate',
  Activity = 'activity',
  OralCommunication = 'oral-communication',
  Comprehension = 'comprehension',
  SubQuestion = 'sub-question',
}

export enum QuestionSubType {
  // Fill in the Blanks (FTB)
  // Math Specific
  SingleBlank = 'single-blank',
  SingleBlankWithPlaceholder = 'single-blank-with-options',

  // Match the Column (MTC)
  // Math Specific
  PictureColumns = 'picture-columns',
  TextColumns = 'text-columns',
  PictureToTextColumns = 'picture-to-text-columns',
  TextToPictureColumns = 'text-to-picture-columns',
  MatchTheColumnsPictureToPictureColumns = 'match-the-columns-picture-to-picture-columns',
  MatchTheColumnsTextToPictureColumns = 'match-the-columns-text-to-picture-columns',
  MatchTheColumnsPictureToTextColumns = 'match-the-columns-picture-to-text-columns',

  // Multiple Choice Questions (MCQs)
  // Math Specific
  BlankInStatement = 'blank-in-statement',
  PictureInStem = 'picture-in-stem',
  OptionsAsPicture = 'options-as-picture',
  PictureAndPicture = 'picture-and-picture',
  MultipleChoiceOptionsAsPicture = 'multiple-choice-options-as-picture',
  MultipleChoicePictureAndPicture = 'multiple-choice-picture-and-picture',

  // Restricted Response Questions (RRQs)
  // Math Specific
  PictureDescriptions = 'picture-descriptions',
  Sequencing = 'sequencing',
  CompleteTheSeries = 'complete-the-series',
  MissingGaps = 'missing-gaps',
  NumberRepresentation = 'number-representation', // NR_RRQ
  // Language Specific
  Dictation = 'dictation',
  Underline = 'underline',

  // Short Answer Questions (SAQs)
  // Primary Math Specific
  HorizontalPicture = 'horizontal-picture',
  VerticalPicture = 'vertical-picture',
  HorizontalText = 'horizontal-text',
  VerticalText = 'vertical-text',
  VerticalTextWithPlaceholder = 'vertical-text-with-placeholder',
  VerticalTextWithPreUnits = 'vertical-text-with-pre-units',
  VerticalTextWithUnits = 'vertical-text-with-units',
  Theoretical = 'theoretical',
  // EY Math Specific
  Count = 'count',
  // Language Specific
  MindMap = 'mind-map',

  // Activity Questions
  ColourTheImage = 'colour-the-image',
  Drawing = 'drawing',
  JoinTheDot = 'join-the-dot',
  Puzzle = 'puzzle',

  // Long Answer Questions (LAQs)
  // Primary Math Specific
  PictureWordProblem = 'picture-word-problem',
  TextWordProblem = 'text-word-problem',
  AnalyticalQuestions = 'analytical-questions',
  Geometry = 'geometry',
  // Language Specific
  DescriptiveWriting = 'descriptive-writing',

  // EY Math Specific (SAQ) _ Math Specific (RRQs)
  CountAndWrite = 'count-and-write',

  // Math Specific (MCQs) _ Language Specific (RRQs)
  DirectQuestion = 'direct-question',

  // Language Specific (Open Ended)
  ExtendedResponse = 'extended-response',

  // Can be in any type
  Fraction = 'fraction',
  Table = 'table',
}

export enum ExamTypes {
  ChapterAssessment = 'CHAPTER_ASSESSMENT',
  TermExam = 'TERM_EXAM',
  Screener = 'SCREENER',
}

export enum AssessmentType {
  Eot = 'eot',
  Eoc = 'eoc',
}

export enum QuestionFormats {
  Statement = 'statement',
  Image = 'image',
  StatementImage = 'statement-image',
}

export interface IGetExamsResponse {
  timestamp: string;
  exams: ISyncChangesResponse<IExam>;
}

export interface IGetAllQuestionsResponse {
  timestamp: string | null;
  questions: ISyncChangesResponse<IQuestion>;
}

export {
  IGrade,
  IAnswerOption,
  IQuestion,
  ISelectedOptions,
  INcpSlo,
  IBookChapterSlo,
  IQuestionMedia,
  IQuestionGroup,
  IGroupQuestionMedia,
};
